import type { BrowserTabSnapshot, DevTunnelRecord } from "@cmmd-forge/contracts";

import { localDevServerPreviewPort } from "~/lib/devServerPreviewUrl";
import { browserDisplayUrl, isDevTunnelPreviewUrl } from "./BrowserPreviewPanel.url";
import { hasReusableDevTunnelAccess } from "./BrowserPreviewPanel.session";

export type BrowserAccessTargetKind = "app-preview" | "shared-browser";
export type BrowserAccessTargetSource = "terminal" | "address-bar" | "launch-card" | "session";

export interface BrowserAccessTarget {
  readonly kind: BrowserAccessTargetKind;
  readonly displayUrl: string | null;
  readonly routableUrl: string | null;
  readonly openExternalUrl: string | null;
  readonly source: BrowserAccessTargetSource;
  readonly displayLabel: "App preview" | "Shared browser";
}

function devTunnelRecordForDisplayUrl(
  displayUrl: string,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord>,
): DevTunnelRecord | null {
  const localPort = localDevServerPreviewPort(displayUrl);
  if (!localPort) {
    return null;
  }
  return (
    devTunnelRecords.find(
      (record) =>
        record.localPort === localPort &&
        hasReusableDevTunnelAccess({
          publicUrl: record.publicUrl,
          expiresAt: record.expiresAt,
        }),
    ) ?? null
  );
}

export function devTunnelUrlForDisplayUrl(input: {
  readonly displayUrl: string;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): string | null {
  const record = devTunnelRecordForDisplayUrl(input.displayUrl, input.devTunnelRecords);
  if (!record) {
    return null;
  }
  try {
    const display = new URL(input.displayUrl);
    const publicUrl = new URL(record.publicUrl);
    const basePath = publicUrl.pathname.endsWith("/")
      ? publicUrl.pathname
      : `${publicUrl.pathname}/`;
    const displayPath = display.pathname === "/" ? "" : display.pathname.replace(/^\/+/u, "");
    publicUrl.pathname = `${basePath}${displayPath}`;
    for (const [key, value] of display.searchParams.entries()) {
      publicUrl.searchParams.set(key, value);
    }
    publicUrl.hash = display.hash;
    return publicUrl.toString();
  } catch {
    return record.publicUrl;
  }
}

export function resolveBrowserAccessTarget(input: {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): BrowserAccessTarget {
  const rawUrl = input.activeTab?.url ?? null;
  const displayUrl = rawUrl ? (browserDisplayUrl(rawUrl, input.devTunnelRecords) ?? rawUrl) : null;
  const tunneledDisplayUrl =
    displayUrl && !isDevTunnelPreviewUrl(displayUrl)
      ? devTunnelUrlForDisplayUrl({ displayUrl, devTunnelRecords: input.devTunnelRecords })
      : null;
  const routableUrl =
    rawUrl && isDevTunnelPreviewUrl(rawUrl) ? rawUrl : (tunneledDisplayUrl ?? rawUrl);
  const isAppPreview = Boolean(
    (displayUrl && localDevServerPreviewPort(displayUrl)) ||
    (rawUrl && isDevTunnelPreviewUrl(rawUrl)),
  );

  return {
    kind: isAppPreview ? "app-preview" : "shared-browser",
    displayUrl,
    routableUrl,
    openExternalUrl: routableUrl,
    source: "session",
    displayLabel: isAppPreview ? "App preview" : "Shared browser",
  };
}
